import { Trans } from "@lingui/macro";
import { FC, useCallback, useState } from "react";
import Link from "next/link";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { useVouchers } from "src/hooks/useVouchers";
import { signOut } from "src/utils/nextAuth";
import { segment, SignEvent, SignLocation } from "src/tracking/segment";
import { Button } from "src/atoms/Button";
import { StyledUserMenu } from "./UserMenu.styled";
import { User } from "src/icons";
import { ChevronRight } from "src/icons/ChevronRight";
import {
  DeliveryRegularIcon,
  FlipBackwardIcon,
  HelpCircleIcon,
  LogOutIcon,
  ShoppingBagIcon,
  GiftIcon,
} from "@otrium/icons";
import { GuestOrderLookup } from "src/molecules/GuestOrderLookup";
import type { GuestLookupIntent } from "src/stores/header/header.store";
import { Box } from "@otrium/core";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { locale } from "src/config/app-config";

interface Props {
  onToggle?: () => void;
  guestLookupView?: GuestLookupIntent | null;
  onGuestLookupViewChange?: (view: GuestLookupIntent | null) => void;
}

const UserMenu: FC<Props> = ({
  onToggle,
  guestLookupView,
  onGuestLookupViewChange,
}) => {
  const isLoggedIn = useIsLoggedIn();
  const { storeCreditActive, showGuestReturnForm } = useFeatureFlags();
  const { data: voucherData } = useVouchers(!storeCreditActive);

  const hasAnyCredit =
    voucherData?.vouchers?.some((voucher) => voucher !== null) ?? false;
  const showStoreCredit = storeCreditActive && hasAnyCredit;

  const [showSignOutButtonUntilRedirect, setShowSignOutButtonUntilRedirect] =
    useState<boolean | undefined>(undefined);

  const handleSignStart = useCallback(
    (event: SignEvent) => () => {
      segment.signHandler(event, { location: SignLocation.MY_ACCOUNT });
      onToggle?.();
    },
    [onToggle]
  );

  const handleLogout = useCallback(() => {
    setShowSignOutButtonUntilRedirect(true);
    void signOut();
  }, []);

  if (
    showGuestReturnForm &&
    !isLoggedIn &&
    guestLookupView &&
    onGuestLookupViewChange
  ) {
    return (
      <GuestOrderLookup
        intent={guestLookupView}
        onBack={() => onGuestLookupViewChange(null)}
      />
    );
  }

  return (
    <StyledUserMenu>
      <li>
        <Link
          href={isLoggedIn ? "/my-orders" : "/sign-in?redirect_to=/my-orders"}
          className="UserMenu__link"
          prefetch={false}
          onClick={() => (onToggle ? onToggle() : undefined)}
        >
          <ShoppingBagIcon />
          <Trans>My Orders</Trans>
        </Link>
      </li>
      <li>
        <Link
          href={isLoggedIn ? "/returns" : "/sign-in?redirect_to=/returns"}
          className="UserMenu__link"
          prefetch={false}
          onClick={() => (onToggle ? onToggle() : undefined)}
        >
          <FlipBackwardIcon />
          <Trans>Create a Return</Trans>
        </Link>
      </li>
      <li>
        <Link
          href={isLoggedIn ? "/my-profile" : "/sign-in?redirect_to=/my-profile"}
          prefetch={false}
          className="UserMenu__link"
          data-testid="user-menu-my-profile"
          onClick={() => (onToggle ? onToggle() : undefined)}
        >
          <User />
          <Trans>My Profile</Trans>
        </Link>
      </li>
      {showStoreCredit && (
        <li>
          <Link
            href={
              isLoggedIn ? "/my-o-credit" : "/sign-in?redirect_to=/my-o-credit"
            }
            className="UserMenu__link"
            prefetch={false}
            data-testid="user-menu-my-o-credit"
            onClick={() => (onToggle ? onToggle() : undefined)}
          >
            <GiftIcon />
            <Trans>My O-credit</Trans>
          </Link>
        </li>
      )}
      <li>
        <Link
          href={getI18nRoute({
            route: "/faq",
            locale,
          })}
          className="UserMenu__link"
          target="_blank"
        >
          <HelpCircleIcon />
          <Trans>Customer Service</Trans>
        </Link>
      </li>
      {showGuestReturnForm && !isLoggedIn && onGuestLookupViewChange && (
        <li className="UserMenu__guestSection">
          <span className="UserMenu__guestLabel">
            <Trans>No account needed</Trans>
          </span>
          <button
            type="button"
            className="UserMenu__guestRow"
            data-testid="user-menu-guest-return"
            onClick={() => onGuestLookupViewChange("return")}
          >
            <FlipBackwardIcon />
            <span>
              <Trans>Return or cancel an order</Trans>
            </span>
            <ChevronRight width={16} height={16} />
          </button>
          <button
            type="button"
            className="UserMenu__guestRow"
            data-testid="user-menu-guest-tracking"
            onClick={() => onGuestLookupViewChange("tracking")}
          >
            <DeliveryRegularIcon />
            <span>
              <Trans>Track a delivery</Trans>
            </span>
            <ChevronRight width={16} height={16} />
          </button>
        </li>
      )}
      <li>
        {isLoggedIn || showSignOutButtonUntilRedirect ? (
          <Box
            role="button"
            data-testid="user-menu-logout-button"
            disabled={showSignOutButtonUntilRedirect}
            onClick={handleLogout}
            className="logout"
          >
            <LogOutIcon />
            <Trans>Logout</Trans>
          </Box>
        ) : (
          <div className="UserMenu__actions">
            <Link href="/sign-in" prefetch={false}>
              <Button
                onClick={handleSignStart(SignEvent.SIGN_IN_STARTED)}
                variant="secondary"
                fullWidth
              >
                <Trans>Login</Trans>
              </Button>
            </Link>
            <Link href="/register" prefetch={false}>
              <Button
                onClick={handleSignStart(SignEvent.SIGN_UP_STARTED)}
                variant="primary"
                fullWidth
              >
                <Trans>Create an account</Trans>
              </Button>
            </Link>
          </div>
        )}
      </li>
    </StyledUserMenu>
  );
};

export { UserMenu };
